import React, { useCallback, useEffect, useRef } from 'react';
import styles from './auth.module.css';

const LENGTH = 6;

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/** Six single-digit inputs. Auto-focus next, backspace previous, paste distributes. */
const OtpInput: React.FC<OtpInputProps> = ({ value, onChange, disabled }) => {
  const digits = Array.from({ length: LENGTH }, (_, i) => value[i] ?? '');
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const focusAt = useCallback((index: number) => {
    const i = Math.max(0, Math.min(index, LENGTH - 1));
    refs.current[i]?.focus();
  }, []);

  const setDigit = useCallback(
    (index: number, char: string) => {
      const next = [...digits];
      next[index] = char;
      onChange(next.join('').slice(0, LENGTH));
    },
    [digits, onChange]
  );

  useEffect(() => {
    if (value.length >= LENGTH) focusAt(LENGTH - 1);
  }, [value.length, focusAt]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !digits[index]) {
      e.preventDefault();
      focusAt(index - 1);
      setDigit(index - 1, '');
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusAt(index - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusAt(index + 1);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw.length > 1) {
      const paste = (value + raw).replace(/\D/g, '').slice(0, LENGTH);
      onChange(paste);
      focusAt(paste.length - 1);
      return;
    }
    const char = raw.slice(-1);
    setDigit(index, char);
    if (char) focusAt(index + 1);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const raw = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, LENGTH);
    if (!raw) return;
    onChange(raw);
    focusAt(raw.length - 1);
  };

  return (
    <div className={styles.otpWrap}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={d}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onPaste={handlePaste}
          disabled={disabled}
          className={styles.otpInput}
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
};

export default OtpInput;
